import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Plus, Edit, ChevronLeft, Search, Check, Star, Cog, Wrench, DollarSign, Milestone, Package, Users } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';

const CONTROL_KEYS = [
  {
    id: 'PS01',
    name: 'Internal Processing',
    description: 'Activities performed by internal resources within the organization',
    icon: Users,
    color: 'blue',
    properties: {
      scheduling: true,
      costing: true,
      confirmation: true,
      purchasing: false,
      milestone: false,
    }
  },
  {
    id: 'PS02',
    name: 'External Processing',
    description: 'Activities outsourced to external vendors or subcontractors',
    icon: Package,
    color: 'violet',
    properties: {
      scheduling: true,
      costing: true,
      confirmation: true,
      purchasing: true,
      milestone: false,
    }
  },
  {
    id: 'PS03',
    name: 'Cost Activity',
    description: 'Cost-only activities for overhead allocation and cost tracking',
    icon: DollarSign,
    color: 'emerald',
    properties: {
      scheduling: false,
      costing: true,
      confirmation: false,
      purchasing: false,
      milestone: false,
    }
  },
  {
    id: 'PS04',
    name: 'Service Activity',
    description: 'Service-based activities with service entry sheet integration',
    icon: Wrench,
    color: 'amber',
    properties: {
      scheduling: true,
      costing: true,
      confirmation: true,
      purchasing: true,
      milestone: false,
    }
  },
  {
    id: 'PS05',
    name: 'Milestone',
    description: 'Key project milestones for tracking progress and triggering billing',
    icon: Milestone,
    color: 'rose',
    properties: {
      scheduling: true,
      costing: false,
      confirmation: true,
      purchasing: false,
      milestone: true,
    }
  },
  {
    id: 'PS06',
    name: 'General Costs',
    description: 'General cost activities for miscellaneous project expenses',
    icon: Cog,
    color: 'slate',
    properties: {
      scheduling: false,
      costing: true,
      confirmation: false,
      purchasing: false,
      milestone: false,
    }
  }
];

const colorMap = {
  blue: { bg: 'bg-orange-50', border: 'border-orange-300', activeBorder: 'border-orange-500', text: 'text-orange-700', icon: 'text-orange-600', ring: 'ring-orange-200', badge: 'bg-orange-100 text-orange-700' },
  violet: { bg: 'bg-orange-50', border: 'border-orange-300', activeBorder: 'border-orange-500', text: 'text-orange-700', icon: 'text-orange-600', ring: 'ring-orange-200', badge: 'bg-orange-100 text-orange-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-300', activeBorder: 'border-emerald-500', text: 'text-emerald-700', icon: 'text-emerald-600', ring: 'ring-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-300', activeBorder: 'border-amber-500', text: 'text-amber-700', icon: 'text-amber-600', ring: 'ring-amber-200', badge: 'bg-amber-100 text-amber-700' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-300', activeBorder: 'border-rose-500', text: 'text-rose-700', icon: 'text-rose-600', ring: 'ring-rose-200', badge: 'bg-rose-100 text-rose-700' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-300', activeBorder: 'border-slate-500', text: 'text-slate-700', icon: 'text-slate-600', ring: 'ring-slate-200', badge: 'bg-slate-200 text-slate-700' },
};

const PropertyBadge = ({ label, active }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
    active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400 line-through'
  }`}>
    {label}
  </span>
);

const ControlKeyCard = ({ ck, isSelected, isDefault, onToggle, onSetDefault }) => {
  const colors = colorMap[ck.color];
  const Icon = ck.icon;

  return (
    <div
      data-testid={`control-key-card-${ck.id}`}
      onClick={onToggle}
      className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${
        isSelected
          ? `${colors.bg} ${colors.activeBorder} shadow-sm ring-2 ${colors.ring}`
          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm opacity-60'
      }`}
    >
      {/* Selection indicator */}
      <div className={`absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center ${
        isSelected ? `${colors.badge}` : 'border-2 border-slate-300 bg-white'
      }`}>
        {isSelected && <Check className="h-3 w-3" />}
      </div>

      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`p-2 rounded-lg ${isSelected ? colors.badge : 'bg-slate-100'}`}>
          <Icon className={`h-5 w-5 ${isSelected ? colors.icon : 'text-slate-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-mono text-sm font-bold ${isSelected ? colors.text : 'text-slate-500'}`}>{ck.id}</span>
            {isDefault && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-semibold">
                <Star className="h-3 w-3 fill-amber-500" /> Default
              </span>
            )}
          </div>
          <p className={`text-sm font-semibold mt-0.5 ${isSelected ? 'text-slate-900' : 'text-slate-500'}`}>{ck.name}</p>
        </div>
      </div>

      {/* Description */}
      <p className={`text-xs mb-3 ${isSelected ? 'text-slate-600' : 'text-slate-400'}`}>{ck.description}</p>

      {/* Properties */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <PropertyBadge label="Scheduling" active={ck.properties.scheduling} />
        <PropertyBadge label="Costing" active={ck.properties.costing} />
        <PropertyBadge label="Confirmation" active={ck.properties.confirmation} />
        <PropertyBadge label="Purchasing" active={ck.properties.purchasing} />
        {ck.properties.milestone && <PropertyBadge label="Milestone" active={true} />}
      </div>

      {/* Set Default button */}
      {isSelected && !isDefault && (
        <button
          data-testid={`set-default-${ck.id}`}
          onClick={(e) => { e.stopPropagation(); onSetDefault(); }}
          className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 transition-colors"
        >
          Set as default
        </button>
      )}
    </div>
  );
};

const NetworkProfiles = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('networkprofiles');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    profile_id: '', name: '', description: '', scheduling_type: 'FORWARD',
    default_duration: 1, default_work_days: 8, default_control_key: 'PS01',
    allowed_control_keys: ['PS01'],
    calculate_float: true, allow_reduction: true, max_reduction_percent: 20,
    require_confirmation: true, allow_partial_confirmation: true, status: 'Active'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getNetworkProfiles();
      setProfiles(response.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({
      profile_id: '', name: '', description: '', scheduling_type: 'FORWARD',
      default_duration: 1, default_work_days: 8, default_control_key: 'PS01',
      allowed_control_keys: ['PS01'],
      calculate_float: true, allow_reduction: true, max_reduction_percent: 20,
      require_confirmation: true, allow_partial_confirmation: true, status: 'Active'
    });
    setShowDialog(true);
  };

  const handleEdit = (profile) => {
    setEditMode(true);
    setFormData({
      ...profile,
      description: profile.description || '',
      allowed_control_keys: profile.allowed_control_keys || [profile.default_control_key || 'PS01']
    });
    setShowDialog(true);
  };

  const toggleControlKey = (keyId) => {
    setFormData(prev => {
      const current = prev.allowed_control_keys || [];
      let updated;
      if (current.includes(keyId)) {
        // Don't allow removing the last key
        if (current.length <= 1) {
          toast.error('At least one control key must be selected');
          return prev;
        }
        updated = current.filter(k => k !== keyId);
        // If removing the default key, set a new default
        if (prev.default_control_key === keyId) {
          return { ...prev, allowed_control_keys: updated, default_control_key: updated[0] };
        }
        return { ...prev, allowed_control_keys: updated };
      } else {
        updated = [...current, keyId];
        return { ...prev, allowed_control_keys: updated };
      }
    });
  };

  const setDefaultKey = (keyId) => {
    setFormData(prev => ({ ...prev, default_control_key: keyId }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.profile_id || !formData.name) {
        toast.error('Please fill required fields');
        return;
      }
      if (!formData.allowed_control_keys || formData.allowed_control_keys.length === 0) {
        toast.error('Please select at least one control key');
        return;
      }
      if (editMode) {
        await psAPI.updateNetworkProfile(formData.profile_id, formData);
        toast.success('Network profile updated');
      } else {
        await psAPI.createNetworkProfile(formData);
        toast.success('Network profile created');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const filteredProfiles = profiles.filter(p =>
    p.profile_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="network-profiles-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ps')} data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Network Profiles</h1>
            <p className="text-sm text-slate-500">Configure network and activity defaults</p>
          </div>
          {writable && (<Button onClick={handleCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="new-profile-btn">
            <Plus className="h-4 w-4 mr-2" />New Profile
          </Button>)}
        </div>

        {/* Search */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search profiles..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50" data-testid="search-input" />
            </div>
          </CardContent>
        </Card>

        {/* Profiles Table */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Profile ID</TableHead>
                  <TableHead className="font-semibold text-slate-600">Name</TableHead>
                  <TableHead className="font-semibold text-slate-600">Scheduling</TableHead>
                  <TableHead className="font-semibold text-slate-600">Duration</TableHead>
                  <TableHead className="font-semibold text-slate-600">Control Keys</TableHead>
                  <TableHead className="font-semibold text-slate-600">Status</TableHead>
                  <TableHead className="w-[80px] font-semibold text-slate-600">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <div className="animate-spin h-5 w-5 border-2 border-orange-600 border-t-transparent rounded-full"></div>
                        Loading...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredProfiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      No network profiles found
                    </TableCell>
                  </TableRow>
                ) : filteredProfiles.map((p) => (
                  <TableRow key={p.profile_id} className="group hover:bg-orange-50/50 transition-colors" data-testid={`profile-row-${p.profile_id}`}>
                    <TableCell className="font-mono font-medium text-orange-600">{p.profile_id}</TableCell>
                    <TableCell className="text-slate-700">{p.name}</TableCell>
                    <TableCell><Badge variant="outline">{p.scheduling_type}</Badge></TableCell>
                    <TableCell className="text-slate-600">{p.default_duration} days</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(p.allowed_control_keys || [p.default_control_key]).map(key => {
                          const ck = CONTROL_KEYS.find(c => c.id === key);
                          const isDefault = key === p.default_control_key;
                          return (
                            <span key={key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              ck ? colorMap[ck.color].badge : 'bg-slate-100 text-slate-600'
                            }`}>
                              {isDefault && <Star className="h-2.5 w-2.5 fill-current" />}
                              {key}
                            </span>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.status === 'Active' ? 'default' : 'secondary'}>{p.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <RecordActions doctype="networkprofiles" onView={() => setViewItem(p)} onEdit={() => handleEdit(p)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold" data-testid="dialog-title">
                {editMode ? 'Edit' : 'Create'} Network Profile
              </DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="general" className="space-y-4">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
                <TabsTrigger value="control-keys" data-testid="tab-control-keys">
                  Control Keys ({formData.allowed_control_keys?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
              </TabsList>

              {/* General Tab */}
              <TabsContent value="general" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Profile ID <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.profile_id}
                      onChange={(e) => setFormData({...formData, profile_id: e.target.value.toUpperCase()})}
                      disabled={editMode}
                      className="bg-slate-50"
                      placeholder="e.g., NW_STD"
                      data-testid="form-profile-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name <span className="text-red-500">*</span></Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-slate-50"
                      placeholder="Profile name"
                      data-testid="form-name"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-sm font-medium">Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="bg-slate-50"
                      placeholder="Optional description"
                      data-testid="form-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Scheduling Type</Label>
                    <Select value={formData.scheduling_type} onValueChange={(v) => setFormData({...formData, scheduling_type: v})}>
                      <SelectTrigger className="bg-slate-50" data-testid="form-scheduling"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FORWARD">Forward Scheduling</SelectItem>
                        <SelectItem value="BACKWARD">Backward Scheduling</SelectItem>
                        <SelectItem value="TODAY">From Today</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Default Duration (days)</Label>
                    <Input
                      type="number" min="0"
                      value={formData.default_duration}
                      onChange={(e) => setFormData({...formData, default_duration: parseInt(e.target.value) || 0})}
                      className="bg-slate-50"
                      data-testid="form-duration"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                      <SelectTrigger className="bg-slate-50" data-testid="form-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* Control Keys Tab */}
              <TabsContent value="control-keys" className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Select Control Keys</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Choose which activity types are available in this network profile. At least one required.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs" data-testid="selected-count">
                    {formData.allowed_control_keys?.length || 0} selected
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3" data-testid="control-keys-grid">
                  {CONTROL_KEYS.map(ck => (
                    <ControlKeyCard
                      key={ck.id}
                      ck={ck}
                      isSelected={(formData.allowed_control_keys || []).includes(ck.id)}
                      isDefault={formData.default_control_key === ck.id}
                      onToggle={() => toggleControlKey(ck.id)}
                      onSetDefault={() => setDefaultKey(ck.id)}
                    />
                  ))}
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <Star className="inline h-3 w-3 fill-amber-500 mr-1" />
                    The <strong>default</strong> control key is auto-assigned to new activities. Click "Set as default" on any selected key to change it.
                  </p>
                </div>
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <Card className="border-slate-200">
                    <CardContent className="pt-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-4">Float & Scheduling</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Calculate Float</Label>
                          <Switch
                            checked={formData.calculate_float}
                            onCheckedChange={(v) => setFormData({...formData, calculate_float: v})}
                            data-testid="form-calculate-float"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Allow Reduction</Label>
                          <Switch
                            checked={formData.allow_reduction}
                            onCheckedChange={(v) => setFormData({...formData, allow_reduction: v})}
                            data-testid="form-allow-reduction"
                          />
                        </div>
                        {formData.allow_reduction && (
                          <div className="space-y-2 pl-4 border-l-2 border-slate-200">
                            <Label className="text-xs text-slate-500">Max Reduction %</Label>
                            <Input
                              type="number" min="0" max="100"
                              value={formData.max_reduction_percent}
                              onChange={(e) => setFormData({...formData, max_reduction_percent: parseFloat(e.target.value) || 0})}
                              className="h-8 bg-slate-50 text-sm"
                              data-testid="form-max-reduction"
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-slate-200">
                    <CardContent className="pt-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-4">Confirmation</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Require Confirmation</Label>
                          <Switch
                            checked={formData.require_confirmation}
                            onCheckedChange={(v) => setFormData({...formData, require_confirmation: v})}
                            data-testid="form-require-confirmation"
                          />
                        </div>
                        {formData.require_confirmation && (
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Allow Partial Confirmation</Label>
                            <Switch
                              checked={formData.allow_partial_confirmation}
                              onCheckedChange={(v) => setFormData({...formData, allow_partial_confirmation: v})}
                              data-testid="form-partial-confirmation"
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="dialog-cancel">Cancel</Button>
              <Button onClick={handleSubmit} className="bg-orange-600 hover:bg-orange-700" data-testid="dialog-submit">
                {editMode ? 'Update' : 'Create'} Profile
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default NetworkProfiles;
